import React from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { styled, keyframes } from "@mui/system";

// Animations
const nodePulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.7); }
  50% { opacity: 1; transform: scale(1.75); }
`;

const linePulse = keyframes`
  0%, 100% { opacity: 0.08; stroke-dashoffset: 22; }
  50% { opacity: 0.55; stroke-dashoffset: 0; }
`;

const starTwinkle = keyframes`
  0%, 100% { opacity: 0.08; transform: scale(0.45); }
  45% { opacity: 1; transform: scale(1.65); }
  70% { opacity: 0.35; transform: scale(0.85); }
`;

const slowFloat = keyframes`
  0% { transform: translate3d(0px, 0px, 0px) rotate(0deg); }
  50% { transform: translate3d(-10px, 7px, 0px) rotate(-0.8deg); }
  100% { transform: translate3d(0px, 0px, 0px) rotate(0deg); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.08; transform: scale(0.7); }
  50% { opacity: 0.52; transform: scale(1.35); }
`;

// Helper arrays
const networkNodes = [
  { id: 1, x: 220, y: 30, r: 1.8, d: "0s" },
  { id: 2, x: 140, y: 45, r: 1.2, d: "0.5s" },
  { id: 3, x: 280, y: 60, r: 2.2, d: "1.2s" },
  { id: 4, x: 190, y: 90, r: 1.5, d: "0.8s" },
  { id: 5, x: 110, y: 110, r: 1.1, d: "2.0s" },
  { id: 6, x: 310, y: 120, r: 1.6, d: "1.5s" },
  { id: 7, x: 240, y: 140, r: 2.4, d: "0.2s" },
  { id: 8, x: 160, y: 160, r: 1.4, d: "2.3s" },
  { id: 9, x: 340, y: 30, r: 1.3, d: "1.0s" },
  { id: 10, x: 60, y: 150, r: 1.0, d: "1.8s" },
  { id: 11, x: 270, y: 180, r: 1.2, d: "0.7s" },
];

const networkLines = [
  [1, 2],
  [1, 3],
  [2, 4],
  [3, 4],
  [4, 5],
  [3, 6],
  [4, 7],
  [6, 7],
  [5, 8],
  [7, 8],
  [1, 9],
  [3, 9],
  [5, 10],
  [8, 10],
  [7, 11],
  [6, 11],
];

const starNodes = [
  { x: 30, y: 40, r: 0.4 },
  { x: 80, y: 20, r: 0.6 },
  { x: 120, y: 15, r: 0.5 },
  { x: 170, y: 10, r: 0.7 },
  { x: 240, y: 15, r: 0.4 },
  { x: 300, y: 10, r: 0.8 },
  { x: 320, y: 45, r: 0.5 },
  { x: 330, y: 80, r: 0.6 },
  { x: 280, y: 95, r: 0.4 },
  { x: 335, y: 150, r: 0.7 },
  { x: 290, y: 170, r: 0.5 },
  { x: 220, y: 180, r: 0.6 },
  { x: 170, y: 190, r: 0.4 },
  { x: 110, y: 175, r: 0.8 },
  { x: 70, y: 120, r: 0.5 },
  { x: 40, y: 80, r: 0.6 },
  { x: 90, y: 60, r: 0.4 },
  { x: 150, y: 80, r: 0.5 },
  { x: 210, y: 55, r: 0.7 },
  { x: 250, y: 110, r: 0.4 },
  { x: 200, y: 130, r: 0.6 },
  { x: 130, y: 140, r: 0.5 },
  { x: 20, y: 110, r: 0.4 },
  { x: 320, y: 190, r: 0.5 },
];

const AnimatedNetwork = () => {
  const getNode = (id) => networkNodes.find((node) => node.id === id);

  return (
    <Box
      sx={{
        position: "absolute",
        top: { xs: -20, sm: -30 },
        right: { xs: -40, sm: -60 },
        width: { xs: 280, sm: 340 },
        height: { xs: 170, sm: 200 },
        zIndex: 0,
        pointerEvents: "none",
        opacity: 0.8,
        overflow: "hidden",
        animation: `${slowFloat} 8s ease-in-out infinite`,
        transformOrigin: "top right",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 340 200"
        preserveAspectRatio="xMaxYMin meet"
        sx={{
          width: "100%",
          height: "100%",
          display: "block",
          filter: "drop-shadow(0 0 6px rgba(0, 140, 255, 0.18))",
        }}
      >
        <defs>
          {/* Radial gradient to fade out the network towards the bottom-left */}
          <radialGradient id="networkFade" cx="100%" cy="0%" r="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="42%" stopColor="#ffffff" stopOpacity="0.38" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#8DE8FF" stopOpacity="0.95" />
            <stop offset="45%" stopColor="#00B8FF" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#00B8FF" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g mask="url(#networkMask)">
          <mask id="networkMask">
            <rect width="100%" height="100%" fill="url(#networkFade)" />
          </mask>

          <g>
            {starNodes.map((star, index) => (
              <circle
                key={`star-${index}`}
                cx={star.x}
                cy={star.y}
                r={star.r}
                fill={index % 5 === 0 ? "#ffffff" : "#59C8FF"}
                style={{
                  animation: `${starTwinkle} ${1.8 + (index % 5) * 0.35}s ease-in-out infinite`,
                  animationDelay: `${(index % 4) * 0.5}s`,
                  transformBox: "fill-box",
                  transformOrigin: "center",
                }}
              />
            ))}
          </g>

          <g>
            {networkLines.map(([from, to], index) => {
              const start = getNode(from);
              const end = getNode(to);
              if (!start || !end) return null;

              return (
                <line
                  key={`${from}-${to}`}
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke={index % 4 === 0 ? "#39C4FF" : "#0B7DFF"}
                  strokeWidth={index % 3 === 0 ? 0.55 : 0.28}
                  strokeLinecap="round"
                  strokeDasharray="4 8"
                  style={{
                    animation: `${linePulse} ${2.6 + (index % 4) * 0.45}s ease-in-out infinite`,
                    animationDelay: `${(index % 3) * 0.5}s`,
                  }}
                />
              );
            })}
          </g>

          <g>
            {networkNodes.map((node, index) => (
              <g key={node.id}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r * 5.5}
                  fill="url(#nodeGlow)"
                  style={{
                    animation: `${glowPulse} ${2.8 + (index % 4) * 0.45}s ease-in-out infinite`,
                    animationDelay: node.d,
                    transformBox: "fill-box",
                    transformOrigin: "center",
                  }}
                />
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r}
                  fill={index % 4 === 0 ? "#ffffff" : "#7DE7FF"}
                  style={{
                    animation: `${nodePulse} ${2 + (index % 3) * 0.35}s ease-in-out infinite`,
                    animationDelay: node.d,
                    transformBox: "fill-box",
                    transformOrigin: "center",
                  }}
                />
              </g>
            ))}
          </g>
        </g>
      </Box>
    </Box>
  );
};

const SectionPanel = styled(Box)(({ theme }) => ({
  background: "#050B14",
  border: "1px solid #14283b",
  borderRadius: "16px",
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
}));

const normalizeUrl = (u) => {
  if (!u) return undefined;
  if (/^(https?:)?\/\//i.test(u)) {
    return u.startsWith("//") ? `https:${u}` : u;
  }
  return `https://${String(u).replace(/^\/*/, "")}`;
};

const LemaSystems = ({ businessCard, companyConfig, handleAddToContacts }) => {
  const brandGreen = companyConfig?.primaryColor || "#32C85A";
  const brandBlue = companyConfig?.secondaryColor || "#00B8FF";
  const txtColor = companyConfig?.textColor || "#FFFFFF";
  const mutedTxt = companyConfig?.mutedTextColor || "#8A94A6";

  const phoneHref = businessCard?.phone
    ? `tel:${businessCard.phone}`
    : undefined;
  const emailHref = businessCard?.email
    ? `mailto:${businessCard.email}`
    : undefined;
  const webHref = normalizeUrl(
    businessCard?.website || businessCard?.websiteUrl || companyConfig?.website,
  );
  const linkedinHref = normalizeUrl(
    businessCard?.linkedin ||
      businessCard?.linkedinUrl ||
      companyConfig?.socialIcons?.linkedin,
  );

  const ActionButton = ({ icon: Icon, label, onClick, href }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <IconButton
        href={href}
        onClick={onClick}
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
        sx={{
          width: 58,
          height: 58,
          borderRadius: "14px",
          backgroundColor: "#08101a",
          border: "1px solid #1c3246",
          color: brandGreen,
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            backgroundColor: "rgba(50, 200, 90, 0.08)",
            borderColor: brandGreen,
          },
        }}
      >
        <Icon fontSize="medium" />
      </IconButton>
      <Typography
        sx={{
          fontSize: "0.8rem",
          color: txtColor,
          fontWeight: 500,
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const InfoRow = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "36px 110px 1fr",
              sm: "42px 160px 1fr",
            },
            alignItems: "center",
            columnGap: { xs: 1.5, sm: 2.5 },
            py: 2.2,
            minHeight: 72,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: brandGreen,
            }}
          >
            <Icon sx={{ fontSize: { xs: 26, sm: 28 } }} />
          </Box>

          <Typography
            sx={{
              fontSize: { xs: "0.92rem", sm: "0.98rem" },
              color: mutedTxt,
              fontWeight: 400,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "0.96rem", sm: "1rem" },
              color: txtColor,
              fontWeight: 400,
              lineHeight: 1.45,
              textAlign: "left",
            }}
          >
            {value}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: "#14283b" }} />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#020812",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0, sm: 2 },
        fontFamily: companyConfig?.fontFamily || "'Montserrat', sans-serif",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 650,
          minHeight: { xs: "100vh", sm: "auto" },
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: { xs: 2.5, sm: 4 },
          overflow: "hidden",
        }}
      >
        {/* Top Header Section */}
        <Box sx={{ position: "relative", zIndex: 1, mb: 1 }}>
          <AnimatedNetwork />

          {/* Logo */}
          <Box sx={{ mb: 6, position: "relative", zIndex: 2 }}>
            {companyConfig?.logo ? (
              <img
                src={companyConfig.logo}
                alt={`${companyConfig.name} Logo`}
                style={{ width: "160px", height: "auto" }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    border: "2px solid #fff",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: brandGreen, fontWeight: "bold" }}>
                    LM
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      color: txtColor,
                      lineHeight: 1,
                    }}
                  >
                    LE-MA
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: txtColor,
                      letterSpacing: "2px",
                      lineHeight: 1,
                      mt: 0.5,
                    }}
                  >
                    SYSTEMS
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Profile Area */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              zIndex: 2,
            }}
          >
            <Box sx={{ position: "relative", mb: 3 }}>
              {/* Gradient Ring */}
              <Box
                sx={{
                  position: "absolute",
                  top: -3,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${brandBlue} 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, ${brandGreen} 100%)`,
                  zIndex: 0,
                }}
              />
              <Avatar
                src={businessCard?.profilePicURL}
                alt={businessCard?.name}
                sx={{
                  width: 160,
                  height: 160,
                  border: `4px solid #020812`,
                  position: "relative",
                  zIndex: 1,
                  bgcolor: "#1a1a1a",
                }}
              >
                {!businessCard?.profilePicURL &&
                  (businessCard?.name?.[0] || "U")}
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  backgroundColor: brandGreen,
                  border: "4px solid #020812",
                  zIndex: 2,
                }}
              />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: txtColor,
                mb: 1,
                textAlign: "center",
                letterSpacing: "-0.5px",
              }}
            >
              {businessCard?.name || "Employee Name"}
            </Typography>
            <Typography
              sx={{
                color: brandGreen,
                fontWeight: 500,
                fontSize: "1.1rem",
                mb: 2.5,
                textAlign: "center",
              }}
            >
              {businessCard?.position || "Job Title"}
            </Typography>
            {businessCard?.bio && (
              <Typography
                sx={{
                  color: "#E0E0E0",
                  textAlign: "center",
                  fontSize: "0.95rem",
                  maxWidth: "85%",
                  lineHeight: 1.5,
                  fontWeight: 400,
                }}
              >
                {businessCard.bio}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action Buttons Panel */}
        <SectionPanel sx={{ zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <ActionButton icon={PhoneIcon} label="Call" href={phoneHref} />
            <ActionButton icon={EmailIcon} label="Email" href={emailHref} />
            <ActionButton icon={LanguageIcon} label="Website" href={webHref} />
            <ActionButton
              icon={LinkedInIcon}
              label="LinkedIn"
              href={linkedinHref}
            />
            <ActionButton
              icon={PersonAddIcon}
              label={
                <>
                  Add to
                  <br />
                  Contacts
                </>
              }
              onClick={handleAddToContacts}
            />
          </Box>
        </SectionPanel>

        {/* Info Panel */}
        <SectionPanel sx={{ zIndex: 1, p: { xs: 2, sm: 4 } }}>
          <Box sx={{ mb: 1 }}>
            <InfoRow
              icon={LocationOnOutlinedIcon}
              label="Based In"
              value={businessCard?.baseLocation}
            />
            <InfoRow
              icon={BusinessOutlinedIcon}
              label="Company"
              value={
                businessCard?.companyNameText ||
                businessCard?.company ||
                "Le-Ma Systems"
              }
            />
            <InfoRow
              icon={WorkOutlineIcon}
              label="Department"
              value={businessCard?.department}
            />
            <InfoRow
              icon={CalendarMonthOutlinedIcon}
              label="Experience"
              value={businessCard?.experience}
            />

            <InfoRow
              icon={TranslateIcon}
              label="Languages"
              value={businessCard?.languages}
            />
          </Box>

          {(businessCard?.mission || businessCard?.vision) && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ borderColor: "#14283b", mb: 4 }} />
              <Grid container spacing={4}>
                {businessCard?.mission && (
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <TrackChangesIcon
                        sx={{ color: brandGreen, fontSize: 24 }}
                      />
                      <Typography
                        sx={{
                          color: brandGreen,
                          fontWeight: 600,
                          fontSize: "1rem",
                        }}
                      >
                        Mission
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: "#E0E0E0",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {businessCard.mission}
                    </Typography>
                  </Grid>
                )}
                {businessCard?.vision && (
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <VisibilityOutlinedIcon
                        sx={{ color: brandGreen, fontSize: 24 }}
                      />
                      <Typography
                        sx={{
                          color: brandGreen,
                          fontWeight: 600,
                          fontSize: "1rem",
                        }}
                      >
                        Vision
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: "#E0E0E0",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {businessCard.vision}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </SectionPanel>

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 2, mb: 2, zIndex: 1 }}>
          <Typography
            sx={{
              color: "#E0E0E0",
              fontWeight: 400,
              fontSize: "1rem",
              mb: 2,
              px: 2,
            }}
          >
            {companyConfig?.footerSlogan ||
              "Building Scalable Digital Systems\nfor Businesses and Institutions"}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                width: "24px",
                height: "2px",
                backgroundColor: brandGreen,
                borderRadius: 2,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ color: mutedTxt, fontSize: "0.75rem" }}>
              © {new Date().getFullYear()}{" "}
              {companyConfig?.legalName || "Le-Ma Systems"}. All rights
              reserved.
            </Typography>
            <Typography
              sx={{
                color: mutedTxt,
                fontSize: "0.75rem",
                display: { xs: "none", md: "block" },
              }}
            >
              |
            </Typography>
            <Typography sx={{ color: mutedTxt, fontSize: "0.75rem" }}>
              {companyConfig?.tagline ||
                "Redefining the digital age through technology that empowers."}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LemaSystems;
